import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";
import {
  isValidEmail,
  isValidPassword,
  strToHtml,
  toE164Kr,
} from "../helpers/utils";
import { REST_API_URL } from "../constants";
import API from "../service/API";
import { modal } from "../modal/ModalService";
import _ from "lodash";
import { useLang } from "../context/LangProvider";
import CountryDropDown from "../components/CountryDropDown";
import LanguageDropdown from "../helpers/LanguageDropdown";
import logoImage from "../img/granhand_logo.png";

const IdStepRender = React.memo(
  ({
    step,
    onAuthoize,
    onPasswordTypeChange,
    account,
    onSigninGo,
    onAuthorizeAgain,
    onSignup,
    i18n,
    lang,
    countryCode,
    onCountrySelect,
    phone,
    setPhone,
    setCodeSent,
    onSendPhoneAuthCode,
    phoneAuthCode,
    isSending,
    setPhoneAuthCode,
    onConfrimPhoneAuthCode,
  }) => {
    switch (step) {
      case 1:
        return lang === "ko" ? (
          <section className="signup-auth__step">
            <div className="signup-auth__summary">
              <p className="signup-auth__summary-title">
                {i18n("find.id.title")}
              </p>
              <p className="signup-auth__summary-desc">
                {i18n("find.id.desc.1")}
                <br />
                {i18n("find.id.desc.2")}
              </p>
            </div>
            <form className="signup-auth__form">
              <button
                type="button"
                onClick={onAuthoize}
                className="signup-auth__submit"
              >
                {i18n("common.verifyPhone")}
              </button>
            </form>
          </section>
        ) : (
          <section className="signup-auth__step">
            <div className="signup-auth__summary">
              <p className="signup-auth__summary-title">
                {i18n("signup.foreigner.verifyPhone")}
              </p>
            </div>

            <div className="signup-auth__form">
              <CountryDropDown onCountrySelect={onCountrySelect} />
              <div className="find-auth__phone-row">
                {countryCode ? `+${countryCode}` : null}
                <input
                  id="phone"
                  type="text"
                  pattern="[0-9]*"
                  className="signup-auth__input"
                  placeholder={i18n("signup.foreigner.phonePlaceholder")}
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    setCodeSent(false);
                  }}
                />
              </div>
              <button
                type="button"
                onClick={onSendPhoneAuthCode}
                disabled={!phone || isSending}
                aria-disabled={!phone || isSending}
                className="signup-auth__submit"
              >
                {i18n("signup.foreigner.requestCode")}
              </button>
              <div className="signup-auth__field">
                <label className="signup-auth__label" htmlFor="phoneAuthCode">
                  {i18n("signup.foreigner.codeLabel")}
                </label>
                <input
                  id="phoneAuthCode"
                  type="text"
                  className="signup-auth__input"
                  placeholder={i18n("signup.foreigner.codePlaceholder")}
                  value={phoneAuthCode}
                  onChange={(e) => setPhoneAuthCode(e.target.value)}
                />
              </div>
              <button
                type="button"
                disabled={!phone || !phoneAuthCode || isSending}
                aria-disabled={!phone || !phoneAuthCode || isSending}
                onClick={onConfrimPhoneAuthCode}
                className="signup-auth__submit"
              >
                {i18n("common.next")}
              </button>
            </div>
          </section>
        );
      case 2: {
        let isSocial = account?.["custom:provider"];
        const socialName =
          account?.["custom:provider"] === "KAKAO" ? "카카오" : "네이버";
        return account ? (
          isSocial ? (
            <section className="signup-auth__step">
              <div className="signup-auth__summary">
                <p className="signup-auth__summary-title">
                  {i18n("find.id.found.title")}
                </p>
              </div>

              <div className="find-auth__card">
                <div className="find-auth__account">
                  <p className="find-auth__account-email">
                    {account?.email || ""}
                  </p>
                  <p className="find-auth__account-meta">
                    {`SNS 계정(${socialName})으로 인증된 아이디입니다.`}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={onSigninGo}
                className="signup-auth__submit"
              >
                {i18n("signin.cta.login")}
              </button>

              <ul className="find-auth__notice">
                <li>{i18n("find.id.notice.noResetForSNS")}</li>
                <li>{i18n("find.id.notice.useQuickLogin")}</li>
              </ul>
            </section>
          ) : (
            <section className="signup-auth__step">
              <div className="signup-auth__summary">
                <p className="signup-auth__summary-title">
                  {i18n("find.id.found.title")}
                </p>
              </div>

              <div className="find-auth__card">
                <div className="find-auth__account">
                  <p className="find-auth__account-email">
                    {account?.email || ""}
                  </p>
                  {account?.["custom:createdAt"] ? (
                    <p className="find-auth__account-meta">
                      {moment(account?.["custom:createdAt"]).format(
                        "YYYY.MM.DD"
                      )}{" "}
                      {i18n("existing.joined")}
                    </p>
                  ) : null}
                </div>

                <button
                  type="button"
                  className="find-auth__link"
                  onClick={onPasswordTypeChange}
                  aria-label={i18n("find.id.resetPassword.aria")}
                >
                  <span>{i18n("find.id.resetPassword")}</span>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      d="M9 6l6 6-6 6"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>

              <button
                type="button"
                onClick={onSigninGo}
                className="signup-auth__submit"
              >
                {i18n("signin.cta.login")}
              </button>

              <ul className="find-auth__notice">
                <li>{i18n("find.id.notice.noResetForSNS")}</li>
                <li>{i18n("find.id.notice.useQuickLogin")}</li>
              </ul>
            </section>
          )
        ) : (
          <section className="signup-auth__step">
            <div className="signup-auth__summary">
              <p className="signup-auth__summary-title">
                {i18n("find.id.notFound.title")}
              </p>
              <p className="signup-auth__summary-desc">
                {i18n("find.id.notFound.desc.1")}
                <br />
                {i18n("find.id.notFound.desc.2")}
              </p>
            </div>

            <div className="find-auth__actions">
              <button
                type="button"
                onClick={onAuthorizeAgain}
                className="signup-auth__outline-button"
              >
                {i18n("find.id.notFound.retry")}
              </button>
              <button
                type="button"
                onClick={onSignup}
                className="signup-auth__submit"
              >
                {i18n("signin.cta.signup")}
              </button>
            </div>

            <div className="find-auth__guide">
              {strToHtml(i18n("find.id.notFound.guide"))}
            </div>
          </section>
        );
      }

      default:
        return null;
    }
  }
);

const PasswordStepRender = React.memo(
  ({
    step,
    onAuthoize,
    email,
    setEmail,
    password,
    rePassword,
    setPassword,
    setRepassword,
    onPasswordSave,
    busy,
    i18n,
    lang,
    countryCode,
    onCountrySelect,
    phone,
    setPhone,
    setCodeSent,
    onSendPhoneAuthCode,
    phoneAuthCode,
    isSending,
    setPhoneAuthCode,
    onConfrimPhoneAuthCode,
    account,
    onAuthorizeAgain,
    onSignup,
    onSigninGo,
  }) => {
    switch (step) {
      case 1:
        return lang === "ko" ? (
          <section className="signup-auth__step">
            <div className="signup-auth__summary">
              <p className="signup-auth__summary-title">
                {i18n("find.password.title")}
              </p>
              <p className="signup-auth__summary-desc">
                {i18n("find.password.desc.1")}
                <br />
                {i18n("find.password.desc.2")}
              </p>
            </div>

            <form className="signup-auth__form">
              <div className="signup-auth__field">
                <label className="signup-auth__label" htmlFor="email">
                  {i18n("signup.form.email.label")}
                </label>
                <input
                  id="email"
                  type="email"
                  className="signup-auth__input"
                  placeholder={i18n("signup.form.email.placeholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                />
              </div>
              <button
                type="button"
                onClick={onAuthoize}
                disabled={!isValidEmail(email)}
                aria-disabled={!isValidEmail(email)}
                className="signup-auth__submit"
              >
                {i18n("common.verifyPhone")}
              </button>
            </form>
          </section>
        ) : (
          <section className="signup-auth__step">
            <div className="signup-auth__summary">
              <p className="signup-auth__summary-title">
                {i18n("signup.foreigner.verifyPhone")}
              </p>
            </div>

            <div className="signup-auth__form">
              <CountryDropDown onCountrySelect={onCountrySelect} />
              <div className="find-auth__phone-row">
                {countryCode ? `+${countryCode}` : null}
                <input
                  id="phone"
                  type="text"
                  pattern="[0-9]*"
                  className="signup-auth__input"
                  placeholder={i18n("signup.foreigner.phonePlaceholder")}
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    setCodeSent(false);
                  }}
                />
              </div>
              <button
                type="button"
                onClick={onSendPhoneAuthCode}
                disabled={!phone || isSending}
                aria-disabled={!phone || isSending}
                className="signup-auth__submit"
              >
                {i18n("signup.foreigner.requestCode")}
              </button>
              <div className="signup-auth__field">
                <label className="signup-auth__label" htmlFor="phoneAuthCode">
                  {i18n("signup.foreigner.codeLabel")}
                </label>
                <input
                  id="phoneAuthCode"
                  type="text"
                  className="signup-auth__input"
                  placeholder={i18n("signup.foreigner.codePlaceholder")}
                  value={phoneAuthCode}
                  onChange={(e) => setPhoneAuthCode(e.target.value)}
                />
              </div>
              <button
                type="button"
                disabled={!phone || !phoneAuthCode || isSending}
                aria-disabled={!phone || !phoneAuthCode || isSending}
                onClick={onConfrimPhoneAuthCode}
                className="signup-auth__submit"
              >
                {i18n("common.next")}
              </button>
            </div>
          </section>
        );
      case 2:
        let isSocial = account?.["custom:provider"];
        const socialName =
          account?.["custom:provider"] === "KAKAO" ? "카카오" : "네이버";
        return account ? (
          <>
            {isSocial ? (
              <section className="signup-auth__step">
                <div className="signup-auth__summary">
                  <p className="signup-auth__summary-title">
                    {i18n("find.password.reset.title")}
                  </p>
                </div>

                <div className="find-auth__card">
                  <div className="find-auth__account">
                    <p className="find-auth__account-email">
                      {account?.email || ""}
                    </p>
                    <p className="find-auth__account-meta">
                      {`SNS 계정(${socialName})으로 인증된 아이디입니다.`}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={onSigninGo}
                  className="signup-auth__submit"
                >
                  {i18n("signin.cta.login")}
                </button>

                <ul className="find-auth__notice">
                  <li>{i18n("find.id.notice.noResetForSNS")}</li>
                  <li>{i18n("find.id.notice.useQuickLogin")}</li>
                </ul>
              </section>
            ) : (
              <section className="signup-auth__step">
                <div className="signup-auth__summary">
                  <p className="signup-auth__summary-title">
                    {i18n("find.password.reset.title")}
                  </p>
                  <p className="signup-auth__summary-desc">
                    {i18n("find.password.reset.desc.1")}
                    <br />
                    {i18n("find.password.reset.desc.2")}
                  </p>
                </div>

                <form className="signup-auth__form">
                  <div className="signup-auth__field">
                    <label className="signup-auth__label" htmlFor="password">
                      {i18n("signup.form.password.label")}
                    </label>
                  <input
                    id="password"
                    type="password"
                    className="signup-auth__input"
                    placeholder={i18n("signup.form.password.placeholder")}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                  />
                  </div>
                  <div className="signup-auth__field">
                    <label className="signup-auth__label" htmlFor="rePassword">
                      {i18n("signup.form.passwordConfirm.label")}
                    </label>
                  <input
                    id="rePassword"
                    type="password"
                    className="signup-auth__input"
                    placeholder={i18n(
                      "signup.form.passwordConfirm.placeholder"
                    )}
                    value={rePassword}
                    onChange={(e) => setRepassword(e.target.value)}
                    autoComplete="current-password"
                  />
                  </div>
                  <button
                    type="button"
                    onClick={onPasswordSave}
                    disabled={
                      !isValidPassword(password) ||
                      password !== rePassword ||
                      busy
                    }
                    aria-disabled={
                      !isValidPassword(password) ||
                      password !== rePassword ||
                      busy
                    }
                    className="signup-auth__submit"
                  >
                    {i18n("find.password.reset.cta")}
                  </button>
                </form>
              </section>
            )}
          </>
        ) : (
          <section className="signup-auth__step">
            <div className="signup-auth__summary">
              <p className="signup-auth__summary-title">
                {i18n("find.id.notFound.title")}
              </p>
              <p className="signup-auth__summary-desc">
                {i18n("find.id.notFound.desc.1")}
                <br />
                {i18n("find.id.notFound.desc.2")}
              </p>
            </div>

            <div className="find-auth__actions">
              <button
                type="button"
                onClick={onAuthorizeAgain}
                className="signup-auth__outline-button"
              >
                {i18n("find.id.notFound.retry")}
              </button>
              <button
                type="button"
                onClick={onSignup}
                className="signup-auth__submit"
              >
                {i18n("signin.cta.signup")}
              </button>
            </div>

            <div className="find-auth__guide">
              {strToHtml(i18n("find.id.notFound.guide"))}
            </div>
          </section>
        );

      default:
        return null;
    }
  }
);

const FindAccount = () => {
  const nav = useNavigate();
  const location = useLocation();
  const [sp, setSp] = useSearchParams();
  const { i18n, lang } = useLang();
  const [isKorea, setIsKorea] = useState(true);
  const typeRef = useRef(sp.get("type"));
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState(location.state?.email || "");
  const [password, setPassword] = useState("");
  const [rePassword, setRepassword] = useState("");
  const [account, setAccount] = useState(null);
  const [busy, setBusy] = useState(false);
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [isSending, setSending] = useState(false);
  const [veifyCodeSent, setCodeSent] = useState(false);
  const [phoneAuthCode, setPhoneAuthCode] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const country = await API.getCountry();
        setIsKorea(country.isKorea ?? true);
      } catch (err) {
        console.error("Failed to load country", err);
      }
    })();
  }, []);

  const handleBackPress = () => {
    if (step === 1) {
      window.history.back();
    } else {
      setStep(step - 1);
    }
  };

  const handleAuthorize = async () => {
    window.MOBILEOK?.process(`${REST_API_URL}/mok/request`, "WB", "result");
  };

  const handlePasswordSave = async () => {
    setBusy(true);
    if (account?.sub) {
      await API.updatePassword(account.sub, password)
        .then(async () => {
          const ok = await modal.alert({
            message: i18n("find.password.reset.success"),
            okText: i18n("common.ok"),
            backdropClose: false,
          });
          if (ok) nav("/");
          setBusy(false);
        })
        .catch((e) => {
          setBusy(false);
          modal.alert({
            message: i18n("find.password.reset.fail"),
            okText: i18n("common.ok"),
            backdropClose: true,
          });
        });
    } else {
      setBusy(false);
    }
  };

  const handlePasswordTypeChange = () => {
    setStep(1);
    setPhoneAuthCode("");
    typeRef.current = "password";
  };

  const handleSignInGo = () => window.history.back();
  const handleAuthorizeAgain = () => setStep(1);
  const handleSignup = () => nav("/signup");

  const checkUser = async (data) => {
    try {
      const userPhone = toE164Kr(data.phone);
      const u = await API.checkUser(null, userPhone, null, null);
      if (!u) {
        await modal.alert({
          message: i18n("find.common.noSignup"),
          okText: i18n("common.ok"),
          backdropClose: true,
        });
      } else {
        setStep(2);
        setAccount(u.attributes || {});
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleConfrmPhoneAuthCode = async () => {
    try {
      const isValid = await API.confirmPhoneAuthCode(
        phoneAuthCode,
        phone,
        countryCode
      );
      if (isValid) {
        try {
          const userPhone = `+${countryCode}${phone}`;
          const u = await API.checkUser(null, userPhone, null, null);
          if (!u) {
            await modal.alert({
              message: i18n("find.common.noSignup"),
              okText: i18n("common.ok"),
              backdropClose: true,
            });
          } else {
            setStep(2);
            setAccount(u.attributes || {});
          }
        } catch (err) {
          console.log(err);
        }
      } else {
        modal.alert({
          message: i18n("signup.foreigner.incorrectCode"),
          okText: i18n("common.ok"),
          backdropClose: true,
        });
      }
    } catch (err) {
      modal.alert({
        message: i18n("common.tryAgain"),
        okText: i18n("common.ok"),
        backdropClose: true,
      });
    }
  };

  const handleCountrySelect = (country) => {
    setCountryCode(country.dialCode);
  };

  const handleSendPhoneAuthCode = async () => {
    try {
      setSending(true);
      await API.sendPhoneAuthCode(phone, countryCode);
      setCodeSent(true);
      setSending(false);
    } catch (err) {
      setSending(false);
    }
  };

  useEffect(() => {
    window.result = async function (payload) {
      fetch(
        `${REST_API_URL}/mok/result?txId=${encodeURIComponent(
          JSON.parse(payload).txId
        )}`
      )
        .then((r) => r.json())
        .then((json) => {
          checkUser(json);
          if (json) {
            if (typeRef.current === "password") {
              setStep(2);
            } else {
            }
          }
        });
    };

    return () => {
      delete window.result;
    };
  }, []);

  return (
    <main className="signup-auth container">
      <div className="signup-auth__frame">
        <div className="signup-auth__toolbar">
          <button
            type="button"
            className="signup-auth__back"
            onClick={handleBackPress}
            aria-label={i18n("signin.nav.back.aria")}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M15 18l-6-6 6-6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>
              {step === 1 ? i18n("signin.nav.back") : i18n("common.prevStep")}
            </span>
          </button>
          <div className="signup-auth__lang">
            <LanguageDropdown />
          </div>
        </div>

        <div className="signup-auth__brand">
          <img alt="GRANHAND." src={logoImage} />
        </div>

        <section className="signup-auth__content">
          {typeRef.current === "password" ? (
            <PasswordStepRender
              step={step}
              email={email}
              account={account}
              setEmail={setEmail}
              onAuthoize={handleAuthorize}
              password={password}
              rePassword={rePassword}
              setRepassword={setRepassword}
              setPassword={setPassword}
              onPasswordSave={handlePasswordSave}
              busy={busy}
              i18n={i18n}
              lang={lang}
              countryCode={countryCode}
              onCountrySelect={handleCountrySelect}
              phone={phone}
              setPhone={setPhone}
              setCodeSent={setCodeSent}
              onSendPhoneAuthCode={handleSendPhoneAuthCode}
              isSending={isSending}
              phoneAuthCode={phoneAuthCode}
              setPhoneAuthCode={setPhoneAuthCode}
              onConfrimPhoneAuthCode={handleConfrmPhoneAuthCode}
              onSignup={handleSignup}
              onSigninGo={handleSignInGo}
              onAuthorizeAgain={handleAuthorizeAgain}
            />
          ) : (
            <IdStepRender
              step={step}
              onAuthoize={handleAuthorize}
              account={account}
              onPasswordTypeChange={handlePasswordTypeChange}
              onSigninGo={handleSignInGo}
              onAuthorizeAgain={handleAuthorizeAgain}
              onSignup={handleSignup}
              i18n={i18n}
              lang={lang}
              countryCode={countryCode}
              onCountrySelect={handleCountrySelect}
              phone={phone}
              setPhone={setPhone}
              setCodeSent={setCodeSent}
              onSendPhoneAuthCode={handleSendPhoneAuthCode}
              isSending={isSending}
              phoneAuthCode={phoneAuthCode}
              setPhoneAuthCode={setPhoneAuthCode}
              onConfrimPhoneAuthCode={handleConfrmPhoneAuthCode}
            />
          )}
        </section>
      </div>
    </main>
  );
};

export default FindAccount;
